/**
 * Groundworks BOQ Summary Computer
 *
 * Computes all groundworks BOQ summary sections (quantities, rates, amounts)
 * from groundworks table data + item library, for persistence in billOfQuantities.groundworks.summary.
 */

import { ItemLibraryResponse } from "../api/itemLibrary";
import type { DynamicBillSummaryPlotMapping, DynamicBillSummaryRunMatrixMapping } from "../types/tender.types";
import { RetainingWallsBuildUpSpecification } from "../api/retainingWallsBuildUp";
import { GeoToberWallBuildUpSpecification } from "../api/wallSectionBuildUp";
import { generateBOQSummaryWithCategories } from "./boqSummaryGenerator";
import {
  wallSpecifications,
  wallFieldToSpecId,
  WALLS_BUILD_UP_ITEM_CODE_MAP,
  isBlockworkBuildUpItem,
  getWallsBuildUpRateMultiplier,
  isFacingBrickworkItem,
  resolveWallsBuildUpItemCode,
} from "../mock/walls-section-build-up";
import { SCREEN_WALL_BOQ_FIELDS, getScreenWallFieldCode } from "./retainingWallsReferenceMapping";
import {
  geoToberWallSpecifications,
  geoToberWallFieldToSpecId,
  GEO_WALL_BUILD_UP_ITEM_CODE_MAP,
  TOBERMORE_BUILD_UP_ITEM_CODE_MAP,
} from "../mock/geo-tober-wall-build-up";
import {
  FOUNDATION_CATEGORIES,
  CUT_FILL_CATEGORIES,
  GARDEN_CATEGORIES,
  PLOT_DRIVES_CATEGORIES,
  PLOT_PAVINGS_CATEGORIES,
  SCAFFOLD_CATEGORIES,
} from "./billSummaryCategories";
import {
  createFoundationsRateLookupMap,
  applyFoundationsRatesToSummary,
  FOUNDATIONS_M3_CONVERSION_FIELDS,
} from "./foundationsItemLibraryIntegration";
import {
  createCutFillsRateLookupMap,
  applyCutFillsRatesToSummary,
} from "./cutFillsItemLibraryIntegration";
import {
  createGardenRateLookupMap,
  applyGardenRatesToSummary,
  GARDEN_M3_CONVERSION_FIELDS,
} from "./gardenItemLibraryIntegration";
import {
  createPlotDrivesRateLookupMap,
  applyPlotDrivesRatesToSummary,
  PLOT_DRIVES_M3_CONVERSION_FIELDS,
} from "./plotDrivesItemLibraryIntegration";
import {
  createPlotPavingsRateLookupMap,
  applyPlotPavingsRatesToSummary,
  PLOT_PAVINGS_M3_CONVERSION_FIELDS,
} from "./plotPavingsItemLibraryIntegration";
import {
  createScaffoldRateLookupMap,
  applyScaffoldRatesToSummary,
  SCAFFOLD_M3_CONVERSION_FIELDS,
} from "./scaffoldItemLibraryIntegration";
import {
  createPlotServiceRateLookupMap,
  applyPlotServiceRatesToSummary,
} from "./plotServiceItemLibraryIntegration";
import { createDrainageRateLookupMap } from "./drainageItemLibraryIntegration";
import {
  OSMA_FOUL_COMBINED_FIELDS,
  OSMA_STORM_COMBINED_FIELDS,
  POLYPIPE_FOUL_COMBINED_FIELDS,
  POLYPIPE_STORM_COMBINED_FIELDS,
} from "./drainageReferenceMapping";
import { reconstructPlotDrivesColumnHeaders } from "./plotDrivesColumnMapping";
import { reconstructPlotPavingsColumnHeaders } from "./plotPavingsColumnMapping";
import { computeBcCalculation } from "../components/common/BcCalculationDetails";

const ALL_M3_CONVERSION_FIELDS: ReadonlySet<string> = new Set([
  ...GARDEN_M3_CONVERSION_FIELDS,
  ...PLOT_PAVINGS_M3_CONVERSION_FIELDS,
  ...PLOT_DRIVES_M3_CONVERSION_FIELDS,
  ...SCAFFOLD_M3_CONVERSION_FIELDS,
  ...FOUNDATIONS_M3_CONVERSION_FIELDS,
]);

export interface BoqSummaryLineItem {
  key: string;
  fieldName: string;
  description: string;
  quantity: number;
  unit: string;
  rate: number;
  amount: number;
  category: string;
  resourceCode?: string;
  plotMapping?: DynamicBillSummaryPlotMapping;
  // Roads & Sewers extra items only (useBillSummaryExtraItems.tsx's
  // `enableRunMatrixMapping` domains) — same round-trip convention as
  // `plotMapping` above, see dynamicBillSummarySerialization.ts.
  runMatrixMapping?: DynamicBillSummaryRunMatrixMapping;
  // Cut & Fill extra items only (useBillSummaryExtraItems.tsx) — a Text-type
  // item (e.g. "Included", "Rate Only") has no qty/rate/amount of its own;
  // carried through so deserializeCutFillsExtraItems can reconstruct it as
  // the same type on reload instead of a zeroed-out Number item.
  amountType?: 'number' | 'text';
  amountText?: string;
  // Unified per-line modifier value. Carries:
  //  - the m³→m² thickness dimension (e.g. "150mm") on M³-conversion lines,
  //  - `item.net × N` on retaining-walls sub-items, where N is the row's rate
  //    multiplier from `WALLS_BUILD_UP_RATE_MULTIPLIERS` — 100mm-blockwork
  //    course count or facing-brickwork half-brick width (e.g. net=90, N=2 → 180);
  //    1 for non-multiplied rows,
  //  - the per-item net rate on geo/tobermore wall sub-items,
  //  - the apartments rate multiplier (≥ 1) on Plot Service apartments lines.
  // Mutually exclusive across the producers, so a line never needs more than one.
  multiplier?: number | string;
  children?: BoqSummaryLineItem[];
}

export interface DrainageSummaryLineItem {
  key: string;
  fieldName?: string;
  description: string;
  quantity: string;
  unit: string;
  rate: string;
  amount: string;
  category: string;
  rowType: string;
  resourceCode?: string;
  sectionAmount?: number;
  // Ratio of the computed per-plot rate to the item-library dbRate.
  // Only set on per-plot section rows (Foul – OSMA / Storm – OSMA /
  // Foul – Polypipe / Storm – Polypipe). Computed as `ratePerPlot ÷ dbRate`
  // so downstream consumers — central-repo `tasks[].resource[]`, Plot
  // Matrix, Excel export — have the rate-inflation factor.
  // Omitted for item rows (NME PPIC, Yard Gully, etc.).
  multiplier?: number;
  children?: DrainageSummaryLineItem[];
  // Drainage Bill Summary's ad-hoc extra items only (useBillSummaryExtraItems.tsx)
  // — same convention as BoqSummaryLineItem's own amountType/amountText/plotMapping,
  // see dynamicBillSummarySerialization.ts's Drainage-specific (DrainageSummaryLineItem
  // uses string quantity/rate/amount, unlike BoqSummaryLineItem's numbers) flatten/
  // deserialize pair.
  amountType?: 'number' | 'text';
  amountText?: string;
  plotMapping?: DynamicBillSummaryPlotMapping;
  runMatrixMapping?: DynamicBillSummaryRunMatrixMapping;
}

export interface GroundworksBoqSummary {
  foundations: BoqSummaryLineItem[];
  cutFills: BoqSummaryLineItem[];
  garden: BoqSummaryLineItem[];
  plotDrives: BoqSummaryLineItem[];
  plotPavings: BoqSummaryLineItem[];
  scaffold: BoqSummaryLineItem[];
  plotService: BoqSummaryLineItem[];
  drainage: DrainageSummaryLineItem[];
  retainingWalls: BoqSummaryLineItem[];
  geoWalls: BoqSummaryLineItem[];
}

function applyPlotServiceQuantityMultipliers(
  items: BoqSummaryLineItem[],
  config?: { plotServicesApartmentsMultiple?: number; serviceConnectionApartmentsMultiple?: number },
): BoqSummaryLineItem[] {
  const plotMultiple = config?.plotServicesApartmentsMultiple ?? 1;
  const scMultiple = config?.serviceConnectionApartmentsMultiple ?? 1;
  return items.map((item) => {
    let multiple = 1;
    if (item.fieldName === 'apartments') multiple = plotMultiple;
    else if (item.fieldName === 'serviceConnectionApartments') multiple = scMultiple;
    else return item;
    const qty = Math.round(item.quantity || 0);
    const newRate = parseFloat(((item.rate || 0) as number * multiple).toFixed(2));
    return {
      ...item,
      rate: newRate,
      amount: parseFloat((qty * newRate).toFixed(2)),
      multiplier: multiple,
    };
  });
}

function computeStandardSection(
  groundworksData: unknown,
  stepName: string,
  categories: Record<string, string[]>,
  rateLookupMap: Map<string, { rate: number; description: string; unit: string }>,
  applyRatesFn: (items: unknown[], rateMap: Map<string, unknown>) => unknown[],
  dimensionValues?: Record<string, string>,
  columnHeaders?: Record<string, string>,
): BoqSummaryLineItem[] {
  const baseItems = generateBOQSummaryWithCategories(
    groundworksData,
    stepName,
    categories,
    dimensionValues,
    null,
    columnHeaders,
  );
  const itemsWithRates = applyRatesFn(baseItems, rateLookupMap as Map<string, unknown>) as BoqSummaryLineItem[];
  return itemsWithRates.map((item, index) => {
    let dimension: string | undefined;
    if (ALL_M3_CONVERSION_FIELDS.has(item.fieldName)) {
      // An unconfigured field falls back to its static label with the
      // "xx(x)mm" placeholder still unresolved (e.g. "xxmm thick 63-10mm Sub
      // Base - Varied Depths") — never extract a dimension from that, even
      // though the fixed portion of some labels (Plot Drives' aggregate sub
      // base ranges, "63-10mm"/"5-20mm") coincidentally contains its own
      // digit+mm substring the regex below would otherwise latch onto.
      const isUnresolvedPlaceholder = /x{2,}\s*mm/i.test(item.description ?? '');
      const dimMatch = isUnresolvedPlaceholder ? null : item.description?.match(/(\d+(?:\.\d+)?mm)/i);
      dimension = dimMatch ? dimMatch[1] : undefined;
    }
    const roundedQty = Math.abs(Math.round(item.quantity || 0));
    const roundedRate = parseFloat(((item.rate || 0) as number).toFixed(2));
    return {
      key: item.key ?? String(index),
      fieldName: item.fieldName,
      description: item.description,
      quantity: roundedQty,
      unit: item.unit,
      rate: roundedRate,
      amount: parseFloat((roundedQty * roundedRate).toFixed(2)),
      category: item.category,
      resourceCode: item.resourceCode,
      ...(dimension !== undefined && { multiplier: dimension }),
      // Only the 6F2 piling mat carries rateMultiplierCode. When Terram is
      // enabled the resourceCode is "ALP031A+GWALP004"; strip ALP031A so only
      // the code whose rate was subject to the m³→m² multiplier is sent.
      ...(item.fieldName === 'pileMat' && item.resourceCode && {
        rateMultiplierCode: item.resourceCode.split('+').filter(p => p !== 'ALP031A').join('+') || item.resourceCode,
      }),
    };
  });
}

function computeDrainageSection(
  groundworksData: unknown,
  rateLookupMap: Map<string, { rate: number; description: string; unit?: string; resourceCode?: string }>,
): DrainageSummaryLineItem[] {
  const gw = groundworksData as Record<string, unknown>;
  const drainageRows: Record<string, unknown>[] =
    (gw?.drainageTableStandard as Record<string, unknown>[]) ||
    (gw?.drainageTable as Record<string, unknown>[]) ||
    [];

  if (drainageRows.length === 0) return [];

  const sumField = (field: string): number =>
    drainageRows.reduce((acc, row) => acc + (parseFloat(row[field] as string) || 0), 0);

  const getRateInfo = (fieldName: string) => rateLookupMap.get(fieldName);
  const getRate = (fieldName: string): number => getRateInfo(fieldName)?.rate || 0;
  const getDescription = (fieldName: string, fallback: string): string =>
    getRateInfo(fieldName)?.description || fallback;

  const hasOsma = drainageRows.some(
    (row) => (parseFloat(row.osma32mmBend as string) || 0) > 0 || (parseFloat(row.osmaStorm32mmBend as string) || 0) > 0,
  );
  const hasPolypipe = drainageRows.some(
    (row) => (parseFloat(row.polypipe32mmBend as string) || 0) > 0 || (parseFloat(row.polypipeStorm32mmBend as string) || 0) > 0,
  );
  const usePolypipe = hasPolypipe && !hasOsma;

  const buildPerPlotRow = (
    key: string,
    fieldName: string,
    fallbackDescription: string,
    category: string,
    combinedFields: readonly string[],
    totalPlots: number,
    nrPlotsTakeOff: number,
  ): DrainageSummaryLineItem => {
    // Per-plot rate = Σ_field (sumField(field) × itemLibraryRate(field)) ÷
    // nrPlotsTakeOff. Mirrors the bill-summary `buildPerPlotRow` in
    // `DrainageBillSummary.tsx`; `combinedFields` enumerates every field that
    // contributes (NME PPICs / Yard Gully / Drainage Channel / Land Drain are
    // emitted below as their own item rows, so they're NOT in this list).
    const combinedSum = combinedFields.reduce(
      (sum, field) => sum + sumField(field) * getRate(field),
      0,
    );
    const rate = nrPlotsTakeOff > 0 ? combinedSum / nrPlotsTakeOff : 0;
    const roundedPlots = Math.abs(Math.round(totalPlots));
    const amount = Math.abs(roundedPlots * parseFloat(rate.toFixed(2)));
    const description = getDescription(fieldName, fallbackDescription);
    // Ratio of the computed per-plot rate to the item-library dbRate.
    // `multiplier = ratePerPlot / dbRate` so downstream consumers can
    // recover the rate-inflation factor applied to the db-side rate.
    // Skipped (undefined) when dbRate is 0 or the per-plot rate is 0
    // to avoid div-by-zero.
    const dbRate = getRate(fieldName);
    const rateValue = parseFloat(rate.toFixed(2));
    const multiplier =
      dbRate > 0 && rateValue > 0
        ? rateValue / dbRate
        : undefined;
    return {
      key,
      fieldName,
      category,
      description,
      quantity: String(roundedPlots),
      unit: 'plot',
      rate: rate.toFixed(2),
      amount: amount.toFixed(2),
      rowType: 'line-item',
      resourceCode: getRateInfo(fieldName)?.resourceCode,
      ...(multiplier !== undefined ? { multiplier } : {}),
    };
  };

  const buildItemRow = (
    key: string,
    fieldName: string,
    fallbackDescription: string,
    category: string,
    unit: string,
    quantityOverride?: number,
  ): DrainageSummaryLineItem => {
    const quantity = quantityOverride !== undefined ? quantityOverride : sumField(fieldName);
    const rateInfo = getRateInfo(fieldName);
    const rate = rateInfo?.rate || 0;
    const description = rateInfo?.description || fallbackDescription;
    const resolvedUnit = rateInfo?.unit || unit;
    const roundedQty = Math.abs(Math.round(quantity));
    const amount = Math.abs(roundedQty * parseFloat(rate.toFixed(2)));
    return {
      key,
      fieldName,
      category,
      description,
      quantity: String(roundedQty),
      unit: resolvedUnit,
      rate: rate.toFixed(2),
      amount: amount.toFixed(2),
      rowType: 'line-item',
      resourceCode: rateInfo?.resourceCode,
    };
  };

  const osmaConfig = (gw?.drainageDimensionValues as Record<string, string>) || {};
  const polypipeConfig = (gw?.drainagePolypipeConfig as Record<string, string>) || {};

  if (usePolypipe) {
    const ppTotalPlots = parseFloat(polypipeConfig.totalPlots) || 0;
    const ppNrPlotsTakeOff = parseFloat(polypipeConfig.nrPlotsTakeOff) || 0;

    return [
      buildPerPlotRow('1.0', 'polypipe32mmBend', 'Foul – Polypipe', 'Polypipe-Foul', POLYPIPE_FOUL_COMBINED_FIELDS, ppTotalPlots, ppNrPlotsTakeOff),
      buildItemRow('1.1', 'polypipeNmePpic', 'NME PPIC', 'Polypipe-Foul', 'Nr'),
      buildItemRow('1.2', 'polypipe600mmNmePpic', '600mm NME PPIC', 'Polypipe-Foul', 'Nr'),
      buildPerPlotRow('2.0', 'polypipeStorm32mmBend', 'Storm – Polypipe', 'Polypipe-Storm', POLYPIPE_STORM_COMBINED_FIELDS, ppTotalPlots, ppNrPlotsTakeOff),
      buildItemRow('2.1', 'polypipeStormNmePpic', 'NME PPIC', 'Polypipe-Storm', 'Nr'),
      buildItemRow('2.2', 'polypipeStorm600mmNmePpic', '600mm NME PPIC', 'Polypipe-Storm', 'Nr'),
      buildItemRow('2.3', 'polypipeStormYardGully', 'Yard Gully', 'Polypipe-Storm', 'Nr', sumField('polypipeStormYardGully') || sumField('yardGully')),
      buildItemRow('2.4', 'polypipeStormDrainageChannel', 'Drainage Channel', 'Polypipe-Storm', 'm', sumField('polypipeStormDrainageChannel') || sumField('drainageChannel')),
      buildItemRow('2.5', 'polypipeStormLandDrain', 'Land Drain', 'Polypipe-Storm', 'm', sumField('polypipeStormLandDrain') || sumField('landDrain')),
    ];
  } else {
    const osmaTotalPlots = parseFloat(osmaConfig.foulTotalPlots) || 0;
    const osmaNrPlotsTakeOff = parseFloat(osmaConfig.foulNrPlotsTakeOff) || 0;

    return [
      buildPerPlotRow('1.0', 'osma32mmBend', 'Foul – OSMA', 'OSMA-Foul', OSMA_FOUL_COMBINED_FIELDS, osmaTotalPlots, osmaNrPlotsTakeOff),
      buildItemRow('1.1', 'osmaNmePpic', 'NME PPIC', 'OSMA-Foul', 'Nr'),
      buildItemRow('1.2', 'osma600mmNmePpic', '600mm NME PPIC', 'OSMA-Foul', 'Nr'),
      buildPerPlotRow('2.0', 'osmaStorm32mmBend', 'Storm – OSMA', 'OSMA-Storm', OSMA_STORM_COMBINED_FIELDS, osmaTotalPlots, osmaNrPlotsTakeOff),
      buildItemRow('2.1', 'osmaStormNmePpic', 'NME PPIC', 'OSMA-Storm', 'Nr'),
      buildItemRow('2.2', 'osmaStorm600mmNmePpic', '600mm NME PPIC', 'OSMA-Storm', 'Nr'),
      buildItemRow('2.3', 'osmaStormYardGully', 'Yard Gully', 'OSMA-Storm', 'Nr', sumField('osmaStormYardGully') || sumField('yardGully')),
      buildItemRow('2.4', 'osmaStormDrainageChannel', 'Drainage Channel', 'OSMA-Storm', 'm', sumField('osmaStormDrainageChannel') || sumField('drainageChannel')),
      buildItemRow('2.5', 'osmaStormLandDrain', 'Land Drain', 'OSMA-Storm', 'm', sumField('osmaStormLandDrain') || sumField('landDrain')),
    ];
  }
}

/** Fields that are the primary quantity measure per category — included as-is, no rate */
const SCAFFOLD_QUANTITY_FIELDS = new Set([
  'scaffoldPlatform', 'loadingBay4x2m', 'stairAccess3x3m',
]);

/** Rate sub-fields per category and how to resolve their description with dimensions */
const SCAFFOLD_RATE_FIELD_GROUPS: Record<string, string[]> = {
  'Scaffold Platforms': ['scaffoldLevelCompact', 'scaffoldGeotextileLayer', 'scaffoldCrush6F2', 'scaffoldType1'],
  'Loading Bays': ['loadingLevelCompact', 'loadingGeotextileLayer', 'loadingCrush6F2', 'loadingType1'],
  'Stair Access': ['stairLevelCompact', 'stairGeotextileLayer', 'stairCrush6F2', 'stairType1'],
};

// Mirrors CONSOLIDATE_FIELDS in ScaffoldBillSummary / SCAFFOLD_CONSOLIDATE in
// calculateScaffoldGrandTotal — the Geotextile Layer rate only contributes to
// the consolidated total when that section's checkbox is on.
const SCAFFOLD_GEOTEXTILE_CONFIG: Record<string, { geotextileField: string; geotextileEnabledKey: string }> = {
  'Scaffold Platforms': { geotextileField: 'scaffoldGeotextileLayer', geotextileEnabledKey: 'scaffoldGeotextileEnabled' },
  'Loading Bays': { geotextileField: 'loadingGeotextileLayer', geotextileEnabledKey: 'loadingGeotextileEnabled' },
  'Stair Access': { geotextileField: 'stairGeotextileLayer', geotextileEnabledKey: 'stairGeotextileEnabled' },
};

function resolveScaffoldFieldDescription(fieldName: string, dimensionValues: Record<string, string>): string {
  if (fieldName.includes('LevelCompact')) return 'Level & Compact';
  if (fieldName.includes('GeotextileLayer')) return 'Geotextile Layer';
  if (fieldName.includes('Crush6F2')) {
    const dim = dimensionValues[fieldName];
    return dim ? `${dim} Crush 6F2` : 'Crush 6F2';
  }
  if (fieldName.includes('Type1')) {
    const dim = dimensionValues[fieldName];
    return dim ? `${dim} Type 1` : 'Type 1';
  }
  return fieldName;
}

/**
 * Same mm-or-metres rule as `getM3ToM2Multiplier` (scaffoldItemLibraryIntegration.ts):
 * value >= 1 is millimetres (÷ 1000), value < 1 is already metres. Used to
 * convert a raw dim string (a `BoqSummaryLineItem.multiplier`) into the
 * factor multiplied into the Scaffold consolidated row's own `multiplier`.
 */
function parseThicknessMetres(raw: unknown): number | null {
  if (raw === undefined || raw === null || raw === '') return null;
  const match = String(raw).trim().match(/^(\d+(?:\.\d+)?)/);
  if (!match) return null;
  const value = parseFloat(match[1]);
  if (!value || value <= 0) return null;
  return value >= 1 ? value / 1000 : value;
}

function computeScaffoldSection(
  groundworksData: unknown,
  itemLibraryData: ItemLibraryResponse,
): BoqSummaryLineItem[] {
  const gw = groundworksData as Record<string, unknown>;
  const dimensionValues: Record<string, string> = (gw?.scaffoldDimensionValues as Record<string, string>) || {};
  const scaffoldColumnHeaders: Record<string, string> = (gw?.scaffoldColumnHeaders as Record<string, string>) || {};

  const rateLookupMap = createScaffoldRateLookupMap(itemLibraryData, groundworksData);

  // Raw code→dbRate lookup (item library only, no dimension/markup logic) — same
  // convention ScaffoldBillSummary's SummaryKey modal uses for computeBcCalculation,
  // so the persisted amount below matches what that modal derives.
  const codeDbRateMap = new Map<string, number>();
  itemLibraryData.forEach((libItem) => {
    const code = libItem.resourceCode?.trim().toUpperCase();
    if (code) codeDbRateMap.set(code, libItem.nettRate || libItem.baseRate || 0);
  });
  const getDbRate = (code: string) => codeDbRateMap.get(code.trim().toUpperCase()) ?? 0;

  const baseItems = generateBOQSummaryWithCategories(
    groundworksData,
    'Scaffold',
    SCAFFOLD_CATEGORIES,
    dimensionValues,
    null,
    scaffoldColumnHeaders,
  );

  const itemsWithRates = applyScaffoldRatesToSummary(baseItems, rateLookupMap) as BoqSummaryLineItem[];

  const result: BoqSummaryLineItem[] = [];
  const processedCategories = new Set<string>();

  itemsWithRates.forEach((item) => {
    const rateGroup = SCAFFOLD_RATE_FIELD_GROUPS[item.category];

    if (!rateGroup) {
      // Category not subject to consolidation — emit as-is, but amount is still the
      // backend-style dbRate-only derivation (computeBcCalculation), same as the
      // consolidated platform rows below.
      const roundedQty = Math.abs(Math.round(item.quantity || 0));
      const roundedRate = parseFloat(((item.rate || 0) as number).toFixed(2));
      const plainItem: BoqSummaryLineItem = { ...item, quantity: roundedQty, rate: roundedRate, amount: parseFloat((roundedQty * roundedRate).toFixed(2)) };
      const plainBc = computeBcCalculation(plainItem, getDbRate);
      result.push({ ...plainItem, rate: plainBc.rate, amount: plainBc.amount });
      return;
    }

    // For categories with rate sub-fields, emit ONE consolidated item per category using
    // the same formula as calculateScaffoldGrandTotal / ScaffoldBillSummary so that
    // computeGrandTotalFromSummary(summary.scaffold) is penny-perfect with Sum Analysis.
    // Rate sub-fields are stored as children so walk() in GroundworksReview can still
    // read their resourceCode and multiplier values.
    if (!SCAFFOLD_QUANTITY_FIELDS.has(item.fieldName)) return;
    if (processedCategories.has(item.category)) return;
    processedCategories.add(item.category);

    const platformQty = item.quantity || 0;
    const roundedPlatformQty = Math.abs(Math.round(platformQty));

    const children: BoqSummaryLineItem[] = [];

    // Geotextile Layer only contributes to the consolidated total when that
    // section's checkbox is on — see SCAFFOLD_GEOTEXTILE_CONFIG. The row
    // itself still stays visible in `children` with its own computed amount.
    const geotextileConfig = SCAFFOLD_GEOTEXTILE_CONFIG[item.category];
    const geotextileEnabled = geotextileConfig
      ? dimensionValues[geotextileConfig.geotextileEnabledKey] === 'true'
      : true;

    // breakdownAmounts mirrors ScaffoldBillSummary breakdown row formula:
    //   rowQty  = rateItem?.quantity ?? platformQty (fallback for zero-quantity sub-fields)
    //   amount  = Math.round(rowQty × rate × 100) / 100
    const breakdownAmounts = rateGroup.map((fieldName, ci) => {
      const rateItem = itemsWithRates.find(
        (i) => i.fieldName === fieldName && i.category === item.category,
      );
      const fallbackRateInfo = rateLookupMap.get(fieldName);
      const rate = ((rateItem?.rate ?? fallbackRateInfo?.rate ?? 0) as number);
      const rowQty = rateItem?.quantity ?? platformQty;
      const breakdownAmount = Math.round(rowQty * rate * 100) / 100;

      const roundedRowQty = Math.abs(Math.round(rowQty));
      const roundedRate = parseFloat(rate.toFixed(2));
      const description = resolveScaffoldFieldDescription(fieldName, dimensionValues);
      const dim = dimensionValues[fieldName];

      children.push({
        key: rateItem?.key ?? `${item.category}-${fieldName}-${ci}`,
        fieldName,
        category: item.category,
        description,
        quantity: roundedRowQty,
        unit: rateItem?.unit ?? fallbackRateInfo?.unit ?? 'm²',
        rate: roundedRate,
        amount: parseFloat((roundedRowQty * roundedRate).toFixed(2)),
        resourceCode: rateItem?.resourceCode ?? fallbackRateInfo?.resourceCode,
        ...(dim && { multiplier: dim }),
      });

      const isGeotextileField = geotextileConfig?.geotextileField === fieldName;
      return isGeotextileField && !geotextileEnabled ? 0 : breakdownAmount;
    });

    // Consolidation formula — identical to calculateScaffoldGrandTotal (no markup):
    //   totalAmount  = Σ Math.round(rowQty × rate × 100) / 100
    //   combinedRate = round(totalAmount / platformQty, 2)
    //   amount       = roundedPlatformQty × combinedRate
    const totalAmount = breakdownAmounts.reduce((s, a) => s + a, 0);
    const combinedRate = platformQty > 0 ? parseFloat((totalAmount / platformQty).toFixed(2)) : 0;
    const consolidatedAmount = Math.abs(roundedPlatformQty * combinedRate);

    // The persisted consolidated row never carries `children` — fold each
    // breakdown component's own resourceCode into one "+"-joined combined
    // code (same convention `computeBcCalculation` already sums for other
    // sections' combined codes). Crush 6F2 and Type1 each have their OWN
    // independently converted thickness (in metres), so they're additionally
    // called out via `rateMultiplierCode` as their two codes "|"-joined, with
    // `multiplier` carrying their two converted-metres factors "|"-joined in
    // the same order — e.g. rateMultiplierCode "GWALP004|GWALP006", multiplier
    // "0.100|0.50" for Crush 6F2 100mm / Type1 50 — so `computeBcCalculation`
    // can scale each one's own dbRate by its own factor instead of a single
    // combined multiplier. Level & Compact / Geotextile Layer have no
    // thickness conversion and stay flat-summed into `resourceCode` only.
    // Geotextile Layer's own code is only added when that section's checkbox
    // is on — same condition `breakdownAmounts` above already applies to its
    // amount contribution — otherwise BC calc would sum its dbRate in even
    // though the live consolidated rate never included it.
    const consolidatedResourceCode = children
      .filter((c) => !(geotextileConfig?.geotextileField === c.fieldName && !geotextileEnabled))
      .map((c) => c.resourceCode)
      .filter(Boolean)
      .join('+');
    const conversionPairs = children
      .filter((c) => SCAFFOLD_M3_CONVERSION_FIELDS.has(c.fieldName))
      .map((c) => ({ code: c.resourceCode, factor: parseThicknessMetres(c.multiplier) }))
      .filter((p): p is { code: string; factor: number } => !!p.code && p.factor !== null);
    const consolidatedRateMultiplierCode = conversionPairs.length > 0
      ? conversionPairs.map((p) => p.code).join('|')
      : undefined;
    const consolidatedMultiplier = conversionPairs.length > 0
      ? conversionPairs.map((p) => p.factor).join('|')
      : undefined;

    const consolidatedItem: BoqSummaryLineItem = {
      key: item.key ?? `${item.category}-platform`,
      fieldName: item.fieldName,
      category: item.category,
      description: item.description,
      quantity: roundedPlatformQty,
      unit: 'm²',
      rate: combinedRate,
      amount: consolidatedAmount,
      resourceCode: consolidatedResourceCode || item.resourceCode,
      ...(consolidatedMultiplier !== undefined && { multiplier: consolidatedMultiplier }),
      ...(consolidatedRateMultiplierCode !== undefined && { rateMultiplierCode: consolidatedRateMultiplierCode }),
    };

    // Persisted rate AND amount are the backend-style dbRate-only derivation
    // (computeBcCalculation — the same formula the SummaryKey modal's "BCCalculation"
    // block shows), not the live breakdownAmounts/combinedRate formula above, so the
    // saved payload matches what that modal derives instead of drifting when
    // rateLookupMap's rate differs from the raw item-library dbRate. Note: this means
    // the persisted rate can differ from calculateScaffoldGrandTotal's own combinedRate
    // (previously kept penny-perfect with Sum Analysis) whenever they disagree.
    const consolidatedBc = computeBcCalculation(consolidatedItem, getDbRate);

    result.push({ ...consolidatedItem, rate: consolidatedBc.rate, amount: consolidatedBc.amount });
  });

  return result;
}

/**
 * Recomputes the 12 (of 16) Subsoil Management cut/fill values that have a live source
 * elsewhere in the groundworks data, rather than a fixed `soilManagementData` snapshot
 * that can go stale the moment a source table changes without CutFills.tsx being
 * revisited. Single source of truth for this formula — both
 * `GroundworksCutFillBillSummary.tsx` (live Qty column) and `computeSoilManagementSection`
 * below (persisted SummaryKey payload) call this SAME function, so the two can never
 * drift apart again. Only the last 4 rows' FILL values (foundationsArisingsFill/
 * plotDrainageFill/wallsFill/plotServicesFill) have no live source and are left for the
 * caller to merge in from `soilManagementData` on top of this return value.
 */
export function computeSoilManagementDerivedCutFillValues(
  groundworksData: unknown,
  persistedRetainingWallsSpecs?: RetainingWallsBuildUpSpecification[],
): {
  foundationsCutArisings: number;
  foundationsFill: number;
  slabPathsCut: number;
  slabPathsFill: number;
  gardensCut: number;
  gardensFill: number;
  drivesCut: number;
  drivesFill: number;
  foundationsArisingsCut: number;
  plotDrainageCut: number;
  wallsCut: number;
  plotServicesCut: number;
} {
  const gw = groundworksData as Record<string, unknown>;
  const cutFillsTable = (gw.cutFillsTable as Record<string, unknown>[]) || [];
  const dataRows = cutFillsTable.filter(
    (r) => r.key !== 'totals' && r.plotNo !== 'TOTALS' && r.plotNo !== 'Total',
  );
  const r3 = (n: number) => Math.round(n * 1000) / 1000;

  const foundationsTable = (gw.foundationsTable as Record<string, unknown>[]) || [];
  const foundationsArisingsCut = r3(
    foundationsTable.reduce((s, row) => s + (Number(row.stockpileArisings) || 0) + (Number(row.depositOnSite) || 0), 0),
  );

  const drainageRows = (gw.drainageTableStandard as Record<string, unknown>[] || gw.drainageTable as Record<string, unknown>[] || []);
  const osmaConf = (gw.drainageDimensionValues as Record<string, string>) || {};
  const ppConf = (gw.drainagePolypipeConfig as Record<string, string>) || {};
  const osmaNr = parseFloat(osmaConf.foulNrPlotsTakeOff ?? '0') || 0;
  const osmaTotal = parseFloat(osmaConf.foulTotalPlots ?? '0') || 0;
  const ppNr = parseFloat(ppConf.nrPlotsTakeOff ?? '0') || 0;
  const ppTotal = parseFloat(ppConf.totalPlots ?? '0') || 0;
  const sumDr = (f: string) => drainageRows.reduce((s, r) => s + (Number(r[f]) || 0), 0);
  const plotDrainageCut = r3(
    (osmaNr > 0 ? (sumDr('tipOnSite') + sumDr('osmaStormTipOnsite')) / osmaNr * osmaTotal : 0) +
    (ppNr > 0 ? (sumDr('polypipeTipOnSite') + sumDr('polypipeStormTipOnSite')) / ppNr * ppTotal : 0),
  );

  const geoWallDims = (gw.geoToberWallBuildUpDimensions as Record<string, Array<{ description?: string; net: number }>>) || {};
  const retWallRows = (gw.retainingWallsTable as Record<string, unknown>[]) || [];
  const geoWallRows = (gw.geoToberWallTable as Record<string, unknown>[]) || [];
  const netByDescBS = (items: Array<{ description?: string; net: number }>, substr: string) =>
    items.filter((i) => (i.description || '').toLowerCase().includes(substr.toLowerCase()))
      .reduce((s, i) => s + (i.net || 0), 0);
  let wallsCutCalc = 0;
  Object.entries(wallFieldToSpecId).forEach(([fieldName, specId]) => {
    const spec = (persistedRetainingWallsSpecs || []).find((s) => s.id === specId);
    const items = spec?.items || [];
    const netPerM = netByDescBS(items, 'stockpile');
    wallsCutCalc += retWallRows.reduce((s, row) => s + (Number(row[fieldName]) || 0), 0) * netPerM;
  });
  Object.entries(geoToberWallFieldToSpecId).forEach(([fieldName, specId]) => {
    const geoItems = geoWallDims[specId] || [];
    const netPerM = netByDescBS(geoItems, 'stockpile');
    wallsCutCalc += geoWallRows.reduce((s, row) => s + (Number(row[fieldName]) || 0), 0) * netPerM;
  });
  const wallsAdditionalExcavation = retWallRows.reduce((s, row) => s + (Number(row.screenWallsAdditionalExcavation) || 0), 0);
  const wallsCut = r3(wallsCutCalc + wallsAdditionalExcavation);

  const svcRows = (gw.plotServiceTable as Record<string, unknown>[]) || [];
  const plotServicesCut = r3(svcRows.reduce((s, r) => s + (Number(r.plots) || 0) + (Number(r.apartments) || 0), 0) * 7);

  return {
    foundationsCutArisings: r3(dataRows.reduce((s, r) => s + (Number(r.cutFounds) || 0), 0)),
    foundationsFill: r3(dataRows.reduce((s, r) => s + (Number(r.fillFounds) || 0), 0)),
    slabPathsCut: r3(dataRows.reduce((s, r) => s + (Number(r.cutPaths) || 0), 0)),
    slabPathsFill: r3(dataRows.reduce((s, r) => s + (Number(r.fillPaths) || 0), 0)),
    gardensCut: r3(dataRows.reduce((s, r) => s + (Number(r.cutGarden) || 0), 0)),
    gardensFill: r3(dataRows.reduce((s, r) => s + (Number(r.fillGarden) || 0), 0)),
    drivesCut: r3(dataRows.reduce((s, r) => s + (Number(r.cutDrives) || 0), 0)),
    drivesFill: r3(dataRows.reduce((s, r) => s + (Number(r.fillDrives) || 0), 0)),
    foundationsArisingsCut,
    plotDrainageCut,
    wallsCut,
    plotServicesCut,
  };
}

function computeSoilManagementSection(
  groundworksData: unknown,
  itemLibraryData: ItemLibraryResponse,
  persistedRetainingWallsSpecs?: RetainingWallsBuildUpSpecification[],
): BoqSummaryLineItem[] {
  const gw = groundworksData as Record<string, unknown>;

  const codeRateLookup = new Map<string, { rate: number; unit: string }>();
  itemLibraryData.forEach((item) => {
    const code = item.resourceCode?.trim().toUpperCase();
    if (code) {
      codeRateLookup.set(code, {
        rate: item.nettRate || item.baseRate || 0,
        unit: item.unit || 'm³',
      });
    }
  });

  const topsoilMgmt = gw.topsoilManagement as { management?: string; subOption?: string; code?: string } | undefined;
  const subsoilMgmt = gw.subsoilManagement as { management?: string; subOption?: string; disposalClassification?: string; code?: string } | undefined;
  const soilData = gw.soilManagementData as Record<string, number> | undefined;
  const cutFillsTable = (gw.cutFillsTable as Record<string, unknown>[]) || [];

  const dataRows = cutFillsTable.filter(
    (r) => r.key !== 'totals' && r.plotNo !== 'TOTALS' && r.plotNo !== 'Total',
  );
  const totalOnsite = dataRows.reduce((s, r) => s + (Number(r.topsoilDisposalOnsite) || 0), 0);
  const totalOffsite = dataRows.reduce((s, r) => s + (Number(r.topsoilDisposalOffsite) || 0), 0);
  const topsoilQty = Math.abs(totalOnsite - totalOffsite);

  const topsoilCode = topsoilMgmt?.code?.trim().toUpperCase() || '';
  const topsoilIsExcluded = topsoilCode === 'NONE' || !topsoilCode;
  const topsoilLibrary = !topsoilIsExcluded ? codeRateLookup.get(topsoilCode) : null;
  // Rounded to 2dp — same convention every other summary section's persisted `rate`
  // uses (e.g. Scaffold's `roundedRate`), and what computeBcCalculation itself rounds
  // dbRate to when re-deriving this for the SummaryKey modal's comparison. Persisting
  // the raw full-precision dbRate here caused a decimal mismatch against that modal.
  const topsoilRate = parseFloat((topsoilLibrary?.rate ?? 0).toFixed(2));
  const topsoilDesc = topsoilIsExcluded
    ? (topsoilCode === 'NONE' ? 'Excluded' : 'Not Configured')
    : (topsoilMgmt?.subOption || topsoilCode);
  const topsoilUnit = topsoilLibrary?.unit ?? 'm³';
  // Rounded to match the Qty column GroundworksCutFillBillSummary.tsx actually displays
  // (`topsoilRoundedQty = Math.round(topsoilQty)`) — persisting the raw unrounded figure
  // made the SummaryKey's own quantity disagree with what's shown on screen.
  const topsoilRoundedQty = Math.round(topsoilQty);
  const topsoilAmount = topsoilIsExcluded ? 0 : parseFloat((topsoilRoundedQty * topsoilRate).toFixed(2));

  // Subsoil qty = |Σcut - Σfill| across 8 rows, using the SAME shared
  // computeSoilManagementDerivedCutFillValues() the live Bill Summary itself calls —
  // guarantees this can never independently drift from the Qty column again. Only the
  // last 4 rows' FILL values have no live source and come from `soilManagementData`.
  const derivedCutFillValues = computeSoilManagementDerivedCutFillValues(groundworksData, persistedRetainingWallsSpecs);
  const computedSoilData: Record<string, number> = {
    ...soilData,
    ...derivedCutFillValues,
  };

  const soilRows = [
    { cutField: 'foundationsCutArisings', fillField: 'foundationsFill' },
    { cutField: 'slabPathsCut', fillField: 'slabPathsFill' },
    { cutField: 'gardensCut', fillField: 'gardensFill' },
    { cutField: 'drivesCut', fillField: 'drivesFill' },
    { cutField: 'foundationsArisingsCut', fillField: 'foundationsArisingsFill' },
    { cutField: 'plotDrainageCut', fillField: 'plotDrainageFill' },
    { cutField: 'wallsCut', fillField: 'wallsFill' },
    { cutField: 'plotServicesCut', fillField: 'plotServicesFill' },
  ];
  const subsoilTotalCut = soilRows.reduce((s, r) => s + (Number(computedSoilData?.[r.cutField]) || 0), 0);
  const subsoilTotalFill = soilRows.reduce((s, r) => s + (Number(computedSoilData?.[r.fillField]) || 0), 0);
  const subsoilQty = Math.abs(subsoilTotalCut - subsoilTotalFill);

  const subsoilCode = subsoilMgmt?.code?.trim().toUpperCase() || '';
  const subsoilIsExcluded = subsoilCode === 'NONE' || !subsoilCode;
  const subsoilLibrary = !subsoilIsExcluded ? codeRateLookup.get(subsoilCode) : null;
  // Rounded to 2dp — see topsoilRate comment above.
  const subsoilRate = parseFloat((subsoilLibrary?.rate ?? 0).toFixed(2));
  const subsoilDescBase = subsoilMgmt?.subOption || '';
  const subsoilDisposalClass = subsoilMgmt?.disposalClassification || '';
  const subsoilDescLabel = subsoilDescBase && subsoilDisposalClass
    ? `${subsoilDescBase} – ${subsoilDisposalClass}`
    : subsoilDescBase;
  const subsoilDesc = subsoilIsExcluded
    ? (subsoilCode === 'NONE' ? 'Excluded' : 'Not Configured')
    : (subsoilDescLabel || subsoilCode);
  const subsoilUnit = subsoilLibrary?.unit ?? 'm³';
  // Rounded to match the Qty column (`subsoilRoundedQty = Math.round(subsoilQty)`) — see
  // topsoilRoundedQty comment above.
  const subsoilRoundedQty = Math.round(subsoilQty);
  const subsoilAmount = subsoilIsExcluded ? 0 : parseFloat((subsoilRoundedQty * subsoilRate).toFixed(2));

  const topsoilFieldName = (topsoilMgmt?.management === 'Surplus' || !topsoilMgmt?.management)
    ? 'topsoilDisposalOffsite'
    : 'topsoilDisposalOnsite';

  return [
    {
      key: 'soil-topsoil-line',
      fieldName: topsoilFieldName,
      description: topsoilDesc,
      quantity: topsoilIsExcluded ? 0 : topsoilRoundedQty,
      unit: topsoilUnit,
      rate: topsoilRate,
      amount: topsoilAmount,
      category: 'Soil Management - Topsoil Management',
      resourceCode: topsoilIsExcluded ? '' : topsoilCode,
    },
    {
      key: 'soil-subsoil-line',
      fieldName: 'soil-subsoil-line',
      description: subsoilDesc,
      quantity: subsoilIsExcluded ? 0 : subsoilRoundedQty,
      unit: subsoilUnit,
      rate: subsoilRate,
      amount: subsoilAmount,
      category: 'Soil Management - Subsoil Management',
      resourceCode: subsoilIsExcluded ? '' : subsoilCode,
    },
  ];
}

function buildItemLibraryCodeRateMap(
  itemLibraryData: ItemLibraryResponse,
): Map<string, { rate: number; description: string; unit: string }> {
  const map = new Map<string, { rate: number; description: string; unit: string }>();
  itemLibraryData.forEach((lib) => {
    const code = lib.resourceCode?.trim().toUpperCase();
    if (code) {
      map.set(code, {
        rate: lib.nettRate || lib.baseRate || 0,
        description: lib.resourceDescription || '',
        unit: lib.unit || '',
      });
    }
  });
  return map;
}

function computeRetainingWallsSection(
  groundworksData: unknown,
  itemLibraryData: ItemLibraryResponse,
  persistedSpecs?: RetainingWallsBuildUpSpecification[],
): BoqSummaryLineItem[] {
  const gw = groundworksData as Record<string, unknown>;
  const tableData = (gw?.retainingWallsTable as Record<string, unknown>[]) || [];
  if (tableData.length === 0) return [];

  const codeRateMap = buildItemLibraryCodeRateMap(itemLibraryData);
  // Raw code→dbRate lookup (item library only) for computeBcCalculation below — same
  // convention Scaffold's computeScaffoldSection uses, so every priced leaf row's
  // rate/amount is the backend-style dbRate-only derivation, not a possibly-stale
  // savedItem.rate snapshot.
  const getDbRate = (code: string) => codeRateMap.get(code.trim().toUpperCase())?.rate ?? 0;
  const wallsBuildUpConfig = gw?.wallsBuildUpConfig as { screenWallsLabel?: string; eoPillarLabel?: string; facingBrickworkLabel?: string } | undefined;
  const screenWallsLabel = wallsBuildUpConfig?.screenWallsLabel;
  const eoPillarLabel = wallsBuildUpConfig?.eoPillarLabel;
  const facingBrickworkLabel = wallsBuildUpConfig?.facingBrickworkLabel;

  const wallTypeTotals: Record<string, number> = {};
  tableData.forEach((row) => {
    wallSpecifications.forEach((spec) => {
      wallTypeTotals[spec.id] = (wallTypeTotals[spec.id] || 0) + ((row[spec.id] as number) || 0);
    });
  });

  const activeSpecs = wallSpecifications.filter((spec) => (wallTypeTotals[spec.id] || 0) > 0);
  const result: BoqSummaryLineItem[] = [];

  // Screen Walls — five standalone fields from the take-off table, priced as
  // `qty × library rate`. Wrapped under a `SCREEN WALLS` sub-header parent so
  // the BoQ JSON mirrors the UI BillSummary layout (sub-header row → field
  // children). Skips zero-qty fields.
  const screenWallChildren: BoqSummaryLineItem[] = [];
  SCREEN_WALL_BOQ_FIELDS.forEach((field) => {
    const qty = tableData.reduce(
      (sum, row) => sum + (Number(row[field.id]) || 0),
      0,
    );
    if (qty <= 0) return;
    const normalizedCode = getScreenWallFieldCode(field, screenWallsLabel, eoPillarLabel).trim().toUpperCase();
    const roundedQty = Math.abs(Math.round(qty));
    const screenWallItem: BoqSummaryLineItem = {
      key: `ret-walls-screen-${field.id}`,
      fieldName: field.id,
      description: field.label,
      quantity: roundedQty,
      unit: field.unit,
      rate: codeRateMap.get(normalizedCode)?.rate || 0,
      amount: 0,
      category: 'Screen Walls',
      resourceCode: normalizedCode,
    };
    const screenWallBc = computeBcCalculation(screenWallItem, getDbRate);
    screenWallChildren.push({ ...screenWallItem, rate: screenWallBc.rate, amount: screenWallBc.amount });
  });

  if (screenWallChildren.length > 0) {
    const screenWallTotal = parseFloat(
      screenWallChildren.reduce((sum, c) => sum + (c.amount || 0), 0).toFixed(2),
    );
    result.push({
      key: 'ret-walls-cat-screen-walls',
      fieldName: 'screen-walls',
      description: 'SCREEN WALLS',
      quantity: 0,
      unit: '',
      rate: 0,
      amount: screenWallTotal,
      category: 'Screen Walls',
      children: screenWallChildren,
    });
  }

  // Wall specs are grouped by their `category` (e.g. "Garden Retaining Walls")
  // so the export emits one sub-header parent per category — matching the UI's
  // collective view ordering.
  const specCategoryRows = new Map<string, BoqSummaryLineItem[]>();
  const pushSpecRow = (categoryName: string, row: BoqSummaryLineItem) => {
    if (!specCategoryRows.has(categoryName)) specCategoryRows.set(categoryName, []);
    specCategoryRows.get(categoryName)!.push(row);
  };

  if (persistedSpecs && persistedSpecs.length > 0) {
    activeSpecs.forEach((spec) => {
      const wallQtyM = wallTypeTotals[spec.id] || 0;
      const roundedWallQtyM = Math.abs(Math.round(wallQtyM));
      const persistedItems = persistedSpecs.find((s) => s.id === spec.id)?.items;
      const itemsToUse = (persistedItems ?? []).filter((item) => {
        const desc = String(item.description ?? '').trim();
        return desc.length > 0 && !/^[0-9.]+$/.test(desc);
      });

      const children: BoqSummaryLineItem[] = [];
      let netPerM = 0;

      itemsToUse.forEach((item, ci) => {
        const isFacingBrick = isFacingBrickworkItem(item.id);
        const evaluateCode = isFacingBrick
          ? resolveWallsBuildUpItemCode(item.id, facingBrickworkLabel)
          : WALLS_BUILD_UP_ITEM_CODE_MAP[item.id];
        const normalizedCode = evaluateCode?.trim().toUpperCase() || '';
        // Some build-up rows share a single library code but stack N
        // multiples of the base unit: 100mm-blockwork course counts
        // (`blockwork_100_0_220` → ×2, …) and facing-brickwork half-brick
        // widths (`facing_bricks_1_wide` → ×2, `_15_wide` → ×3, …) both use
        // the `libraryRate × N` pattern — see `WALLS_BUILD_UP_RATE_MULTIPLIERS`.
        const isMultiplied = isBlockworkBuildUpItem(item.id);
        const rateMultiplier = isMultiplied ? getWallsBuildUpRateMultiplier(item.id) : 1;
        const net = Math.abs(item.net ?? 0);
        // rate/amount are the backend-style dbRate-only derivation (computeBcCalculation,
        // single resourceCode × plain numeric multiplier = net × N) rather than
        // baseRate/netRate above — ignores any stale savedItem.rate snapshot, always
        // uses the current item-library dbRate.
        const buildUpItem: BoqSummaryLineItem = {
          key: `ret-walls-${spec.id}-item-${ci}`,
          fieldName: item.id,
          description: item.description,
          quantity: roundedWallQtyM,
          unit: 'm',
          rate: 0,
          amount: 0,
          category: 'Retaining Walls',
          resourceCode: normalizedCode,
          // `multiplier = item.net × N` where N is the row's rate multiplier
          // (blockwork course count or facing-brickwork half-brick width
          // count, 1 for non-multiplied rows). Lets downstream consumers
          // recover the multiplied net used to derive `rate`
          // without re-deriving N from the row id.
          multiplier: net * rateMultiplier,
        };
        const buildUpBc = computeBcCalculation(buildUpItem, getDbRate);
        netPerM += buildUpBc.rate;

        children.push({
          ...buildUpItem,
          rate: buildUpBc.rate,
          amount: buildUpBc.amount,
        });
      });

      if (children.length === 0) return;

      const roundedNetPerM = parseFloat(netPerM.toFixed(2));
      pushSpecRow(spec.category, {
        key: `ret-walls-spec-${spec.id}`,
        fieldName: spec.id,
        description: spec.name,
        quantity: roundedWallQtyM,
        unit: 'm',
        rate: roundedNetPerM,
        amount: parseFloat((roundedWallQtyM * roundedNetPerM).toFixed(2)),
        category: spec.category,
        children,
      });
    });
  } else {
    // Fallback: use wallsBuildUpDimensions from state (populated on fresh import or API hydration)
    const wallsBuildUpDimensions = (gw?.wallsBuildUpDimensions ?? {}) as Record<string, Array<{ description: string; net: number; unit: string }>>;

    const descriptionToMockId = new Map<string, string>();
    wallSpecifications.forEach((spec) => {
      spec.items.forEach((item) => {
        descriptionToMockId.set(item.description.toLowerCase(), item.id);
      });
    });

    activeSpecs.forEach((spec) => {
      const wallQtyM = wallTypeTotals[spec.id] || 0;
      const roundedWallQtyM = Math.abs(Math.round(wallQtyM));
      const importedItems = wallsBuildUpDimensions[spec.id];
      if (!importedItems || importedItems.length === 0) return;

      const children: BoqSummaryLineItem[] = [];
      let netPerM = 0;

      importedItems
        .filter((imp) => {
          const desc = String(imp.description ?? '').trim();
          return desc.length > 0 && !/^[0-9.]+$/.test(desc);
        })
        .forEach((imp, ci) => {
          const descKey = imp.description.toLowerCase();
          const mockId = descriptionToMockId.get(descKey);
          const id = mockId ?? descKey.replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
          const isFacingBrick = isFacingBrickworkItem(id);
          const evaluateCode = isFacingBrick
            ? resolveWallsBuildUpItemCode(id, facingBrickworkLabel)
            : WALLS_BUILD_UP_ITEM_CODE_MAP[id];
          const normalizedCode = evaluateCode?.trim().toUpperCase() || '';
          // See blockwork / facing-brickwork comment in the persistedSpecs branch above.
          const isMultiplied = isBlockworkBuildUpItem(id);
          const rateMultiplier = isMultiplied ? getWallsBuildUpRateMultiplier(id) : 1;
          const net = Math.abs(imp.net ?? 0);

          // rate/amount are the backend-style dbRate-only derivation (computeBcCalculation,
          // single resourceCode × plain numeric multiplier = net × N) — see persistedSpecs
          // branch above for the same treatment.
          const buildUpItem: BoqSummaryLineItem = {
            key: `ret-walls-${spec.id}-item-${ci}`,
            fieldName: id,
            description: imp.description,
            quantity: roundedWallQtyM,
            unit: 'm',
            rate: 0,
            amount: 0,
            category: 'Retaining Walls',
            resourceCode: normalizedCode,
            // multiplier = item.net × N — see blockwork / facing-brickwork comment above.
            multiplier: net * rateMultiplier,
          };
          const buildUpBc = computeBcCalculation(buildUpItem, getDbRate);
          netPerM += buildUpBc.rate;

          children.push({ ...buildUpItem, rate: buildUpBc.rate, amount: buildUpBc.amount });
        });

      if (children.length === 0) return;

      const roundedNetPerM = parseFloat(netPerM.toFixed(2));
      pushSpecRow(spec.category, {
        key: `ret-walls-spec-${spec.id}`,
        fieldName: spec.id,
        description: spec.name,
        quantity: roundedWallQtyM,
        unit: 'm',
        rate: roundedNetPerM,
        amount: parseFloat((roundedWallQtyM * roundedNetPerM).toFixed(2)),
        category: spec.category,
        children,
      });
    });
  }

  // Emit one sub-header parent per wall category, preserving the order in
  // which `activeSpecs` first introduced each category (matches the UI's
  // collective-view ordering driven by `categoryMap` insertion order).
  specCategoryRows.forEach((rows, categoryName) => {
    const sectionTotal = parseFloat(
      rows.reduce((sum, r) => sum + (r.amount || 0), 0).toFixed(2),
    );
    const slug = categoryName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    result.push({
      key: `ret-walls-cat-${slug}`,
      fieldName: slug,
      description: categoryName.toUpperCase(),
      quantity: 0,
      unit: '',
      rate: 0,
      amount: sectionTotal,
      category: categoryName,
      children: rows,
    });
  });

  return result;
}

// Inverted map: specId → fieldName in geoToberWallTable
const geoSpecIdToField: Record<string, string> = {};
Object.entries(geoToberWallFieldToSpecId).forEach(([field, specId]) => {
  geoSpecIdToField[specId] = field;
});

/**
 * Resolves an imported build-up dimension row to its matching mock spec item, by id
 * first, then by description. A plain `.find()` by description alone breaks when a
 * spec lists 2+ items sharing an identical description — e.g. Tobermore TPCW's two
 * "Stockpile Onsite" rows (`stockpile_onsite`/`stockpile_onsite_2`, one m³ before
 * excavation and one m² after) — since `.find()` always returns the FIRST match for
 * every such row, silently duplicating that first item's identity onto the second row
 * and leaving the second mock item's own id/resourceCode never emitted at all.
 * `occurrenceCounts` (one Map per spec, reset per spec) tracks how many rows with this
 * description have already been resolved, so the Nth duplicate resolves to the Nth
 * spec item sharing that description — matching both the mock template's and the
 * source Excel sheet's row order.
 */
function resolveWallBuildUpMockItem<T extends { id: string; description: string }>(
  specItems: T[],
  dimItem: { id?: string; description: string },
  occurrenceCounts: Map<string, number>,
): T | undefined {
  if (dimItem.id) {
    const byId = specItems.find((i) => i.id === dimItem.id);
    if (byId) return byId;
  }
  const descKey = (dimItem.description ?? '').toLowerCase();
  const matches = specItems.filter((i) => i.description.toLowerCase() === descKey);
  const occurrence = occurrenceCounts.get(descKey) ?? 0;
  occurrenceCounts.set(descKey, occurrence + 1);
  return matches[occurrence] ?? matches[matches.length - 1];
}

function computeGeoWallsSection(
  groundworksData: unknown,
  itemLibraryData: ItemLibraryResponse,
  persistedSpecs?: GeoToberWallBuildUpSpecification[],
): BoqSummaryLineItem[] {
  const gw = groundworksData as Record<string, unknown>;
  const tableData = (gw?.geoToberWallTable as Record<string, unknown>[]) || [];
  if (tableData.length === 0) return [];

  const codeRateMap = buildItemLibraryCodeRateMap(itemLibraryData);
  // Raw code→dbRate lookup (item library only) for computeBcCalculation below — same
  // convention computeRetainingWallsSection/computeScaffoldSection use, so every
  // priced leaf row's rate/amount is the backend-style dbRate-only derivation, not a
  // possibly-stale savedItem.rate/item.rate snapshot.
  const getDbRate = (code: string) => codeRateMap.get(code.trim().toUpperCase())?.rate ?? 0;

  const wallTypeTotals: Record<string, number> = {};
  tableData.forEach((row) => {
    Object.entries(geoToberWallFieldToSpecId).forEach(([fieldName, specId]) => {
      wallTypeTotals[specId] = (wallTypeTotals[specId] || 0) + ((row[fieldName] as number) || 0);
    });
  });

  const activeSpecs = geoToberWallSpecifications.filter((spec) => (wallTypeTotals[spec.id] || 0) > 0);
  const result: BoqSummaryLineItem[] = [];

  // Geo/Tobermore wall specs are grouped by `spec.category` (e.g. "Geo Walls",
  // "Tobermore Walls") so the export emits one sub-header parent per category —
  // matching the UI's collective view layout.
  const specCategoryRows = new Map<string, BoqSummaryLineItem[]>();
  const pushSpecRow = (categoryName: string, row: BoqSummaryLineItem) => {
    if (!specCategoryRows.has(categoryName)) specCategoryRows.set(categoryName, []);
    specCategoryRows.get(categoryName)!.push(row);
  };

  // Live build-up edits (`geoToberWallBuildUpDimensions`, written on every
  // GeoToberWallSectionBuildUp edit) take precedence over the persisted API
  // snapshot for each spec, so the SummaryKey's `multiplier` (= net) tracks
  // unsaved build-up changes immediately instead of only updating after a
  // save + refetch. Falls back to the persisted spec's items only for specs
  // with no live dimensions recorded yet.
  const geoToberWallBuildUpDimensions = (gw?.geoToberWallBuildUpDimensions ?? {}) as Record<string, Array<{ id?: string; description: string; net: number; unit: string }>>;

  activeSpecs.forEach((spec) => {
    const wallQtyM = wallTypeTotals[spec.id] || 0;
    const roundedWallQtyM = Math.abs(Math.round(wallQtyM));
    const codeMap = spec.type === 'geowall' ? GEO_WALL_BUILD_UP_ITEM_CODE_MAP : TOBERMORE_BUILD_UP_ITEM_CODE_MAP;

    const liveItems = geoToberWallBuildUpDimensions[spec.id];
    const persistedItems = persistedSpecs?.find((s) => s.id === spec.id)?.items;
    const sourceItems: Array<{ id?: string; description: string; net?: number }> =
      (liveItems && liveItems.length > 0) ? liveItems : (persistedItems ?? []);
    if (sourceItems.length === 0) return;

    const children: BoqSummaryLineItem[] = [];
    let netPerM = 0;
    const occurrenceCounts = new Map<string, number>();

    // rate/amount for every child below are the backend-style dbRate-only derivation
    // (computeBcCalculation, single resourceCode × plain numeric multiplier = net) —
    // ignores any stale savedItem.rate/item.rate/mockItem.rate snapshot, always uses
    // the current item-library dbRate. Same treatment as computeRetainingWallsSection.
    sourceItems.forEach((item, ci) => {
      const mockItem = resolveWallBuildUpMockItem(spec.items, item, occurrenceCounts);
      if (!mockItem) return;
      const evaluateCode = codeMap[mockItem.id];
      const normalizedCode = evaluateCode?.trim().toUpperCase() || '';
      const net = Math.abs(item.net ?? 0);
      const buildUpItem: BoqSummaryLineItem = {
        key: `geo-walls-${spec.id}-item-${ci}`,
        fieldName: mockItem.id,
        description: mockItem.description,
        quantity: roundedWallQtyM,
        unit: 'm',
        rate: 0,
        amount: 0,
        category: 'Geowall & Tobermore Walls',
        resourceCode: normalizedCode,
        multiplier: net,
      };
      const buildUpBc = computeBcCalculation(buildUpItem, getDbRate);
      netPerM += buildUpBc.rate;
      children.push({ ...buildUpItem, rate: buildUpBc.rate, amount: buildUpBc.amount });
    });

    if (children.length === 0) return;

    const roundedNetPerM = parseFloat(netPerM.toFixed(2));
    pushSpecRow(spec.category, {
      key: `geo-walls-spec-${spec.id}`,
      fieldName: spec.id,
      description: spec.name,
      quantity: roundedWallQtyM,
      unit: 'm',
      rate: roundedNetPerM,
      amount: parseFloat((roundedWallQtyM * roundedNetPerM).toFixed(2)),
      category: spec.category,
      children,
    });
  });

  specCategoryRows.forEach((rows, categoryName) => {
    const sectionTotal = parseFloat(
      rows.reduce((sum, r) => sum + (r.amount || 0), 0).toFixed(2),
    );
    const slug = categoryName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    result.push({
      key: `geo-walls-cat-${slug}`,
      fieldName: slug,
      description: categoryName.toUpperCase(),
      quantity: 0,
      unit: '',
      rate: 0,
      amount: sectionTotal,
      category: categoryName,
      children: rows,
    });
  });

  return result;
}

/**
 * Computes all groundworks BOQ summary sections from groundworks data + item library.
 * Returns a structured object suitable for storage in billOfQuantities.groundworks.summary.
 */
export function computeGroundworksBoqSummary(
  groundworksData: unknown,
  itemLibraryData: ItemLibraryResponse | undefined,
  persistedBuildUp?: { retainingWalls?: RetainingWallsBuildUpSpecification[]; geoWalls?: GeoToberWallBuildUpSpecification[] },
): GroundworksBoqSummary {
  if (!itemLibraryData) {
    return {
      foundations: [],
      cutFills: [],
      garden: [],
      plotDrives: [],
      plotPavings: [],
      scaffold: [],
      plotService: [],
      drainage: [],
      retainingWalls: [],
      geoWalls: [],
    };
  }

  const gw = groundworksData as Record<string, unknown>;

  const gardenDimensionValues = (gw?.gardenDimensionValues as Record<string, string>) || {};
  const plotDrivesDimensionValues = (gw?.plotDrivesDimensionValues as Record<string, string>) || {};
  const rawPlotDrivesColumnHeaders = (gw?.plotDrivesColumnHeaders as Record<string, string>) || {};
  const plotDrivesColumnHeaders = reconstructPlotDrivesColumnHeaders(rawPlotDrivesColumnHeaders, plotDrivesDimensionValues);
  const plotPavingsDimensionValues = (gw?.plotPavingsDimensionValues as Record<string, string>) || {};
  const rawPlotPavingsColumnHeaders = (gw?.plotPavingsColumnHeaders as Record<string, string>) || {};
  const plotPavingsColumnHeaders = reconstructPlotPavingsColumnHeaders(rawPlotPavingsColumnHeaders, plotPavingsDimensionValues);
  return {
    foundations: computeStandardSection(
      groundworksData,
      'Foundations',
      FOUNDATION_CATEGORIES,
      createFoundationsRateLookupMap(itemLibraryData, groundworksData),
      applyFoundationsRatesToSummary as (items: unknown[], rateMap: Map<string, unknown>) => unknown[],
    ),

    cutFills: [
      ...computeStandardSection(
        groundworksData,
        'Cut & Fills',
        CUT_FILL_CATEGORIES,
        createCutFillsRateLookupMap(itemLibraryData),
        applyCutFillsRatesToSummary as (items: unknown[], rateMap: Map<string, unknown>) => unknown[],
      ),
      ...computeSoilManagementSection(groundworksData, itemLibraryData, persistedBuildUp?.retainingWalls),
    ],

    garden: computeStandardSection(
      groundworksData,
      'Garden',
      GARDEN_CATEGORIES,
      createGardenRateLookupMap(itemLibraryData, groundworksData),
      applyGardenRatesToSummary as (items: unknown[], rateMap: Map<string, unknown>) => unknown[],
      gardenDimensionValues,
    ),

    plotDrives: computeStandardSection(
      groundworksData,
      'Plot Drives',
      PLOT_DRIVES_CATEGORIES,
      createPlotDrivesRateLookupMap(itemLibraryData, groundworksData),
      applyPlotDrivesRatesToSummary as (items: unknown[], rateMap: Map<string, unknown>) => unknown[],
      plotDrivesDimensionValues,
      plotDrivesColumnHeaders,
    ),

    plotPavings: computeStandardSection(
      groundworksData,
      'Plot Pavings',
      PLOT_PAVINGS_CATEGORIES,
      createPlotPavingsRateLookupMap(itemLibraryData, groundworksData),
      applyPlotPavingsRatesToSummary as (items: unknown[], rateMap: Map<string, unknown>) => unknown[],
      plotPavingsDimensionValues,
      plotPavingsColumnHeaders,
    ),

    scaffold: computeScaffoldSection(groundworksData, itemLibraryData),

    plotService: applyPlotServiceQuantityMultipliers(
      computeStandardSection(
        groundworksData,
        'Plot Service',
        {
          'Plot Services': [
            'plots', 'apartments', 'eo25mmBarrierPipe', 'eo32mmBarrierPipe',
            'virginMedia', 'garageDuct', 'privateStLightDuct', 'pccEdgingsGasBoxes',
            'ductingToChargingPoint', 'concBaseChargingPoints', 'installBollardBase', 'airSourceHeatPump',
          ],
          'Service Connections': [
            'serviceConnectionPlots', 'serviceConnectionApartments', 'vacEx',
          ],
        },
        createPlotServiceRateLookupMap(itemLibraryData),
        applyPlotServiceRatesToSummary as (items: unknown[], rateMap: Map<string, unknown>) => unknown[],
      ),
      (groundworksData as any)?.plotServiceConfig,
    ),

    drainage: computeDrainageSection(
      groundworksData,
      createDrainageRateLookupMap(itemLibraryData, groundworksData),
    ),

    retainingWalls: computeRetainingWallsSection(groundworksData, itemLibraryData, persistedBuildUp?.retainingWalls),

    geoWalls: computeGeoWallsSection(groundworksData, itemLibraryData, persistedBuildUp?.geoWalls),
  };
}

/**
 * Computes the groundworks grand total directly from the summary sections.
 * Uses top-level item amounts (parent amount for wall specs with children).
 * This ensures grandTotal and summary are always derived from the same calculation.
 */
export function computeGrandTotalFromSummary(summary: GroundworksBoqSummary): number {
  let total = 0;

  const addBoqItems = (items: BoqSummaryLineItem[]) => {
    items.forEach(item => { total += item.amount || 0; });
  };

  addBoqItems(summary.foundations);
  addBoqItems(summary.cutFills);
  addBoqItems(summary.garden);
  addBoqItems(summary.plotDrives);
  addBoqItems(summary.plotPavings);
  addBoqItems(summary.scaffold);
  addBoqItems(summary.plotService);
  addBoqItems(summary.retainingWalls);
  addBoqItems(summary.geoWalls);

  summary.drainage.forEach(item => {
    total += parseFloat(item.amount || '0');
  });

  return parseFloat(total.toFixed(2));
}
